import React, { FC } from 'react';

import TrustPilot, { SizeOverride } from '@rover/components/src/Trustpilot';
import type { BoxProps, DisplayProps, HeadingProps, ImageProps } from '@rover/kibble/core';
import { Box, Display, Flex, Heading, Image } from '@rover/kibble/core';
import type { FlexboxSystemProps } from '@rover/kibble/core/styledSystemPropTypes';
import Section from '@rover/kibble/patterns/Section';

import defaultHeroImageDesktop from '../ServiceLanding/pages/shared-images/Hero_Generic_desktop.jpg';
import defaultHeroImageMobile from '../ServiceLanding/pages/shared-images/Hero_Generic_mobile.jpg';
import defaultHeroImageTablet from '../ServiceLanding/pages/shared-images/Hero_Generic_tablet.jpg';

import LandingSearchForm from './LandingSearchForm';

type SearchFormProps = React.ComponentProps<typeof LandingSearchForm>;
type TrustPilotProps = React.ComponentProps<typeof TrustPilot>;

export type HeroImageSrcSet = {
  desktop: {
    src: string;
    height: number;
  };
  tablet: {
    src: string;
    height: number;
  };
  mobile?: {
    src: string;
    height: number;
  };
};

export type LandingHeroProps = {
  upliftedContent?: boolean;
  centerContent?: boolean;
  heroImageSrcSet?: HeroImageSrcSet;
  imageProps?: Partial<ImageProps>;
} & BoxProps;

type LandingHeroSubcomponents = {
  Content: FC<BoxProps & { upliftedContent?: boolean }>;
  Display: FC<DisplayProps>;
  Text: FC<HeadingProps>;
  SearchForm: FC<SearchFormProps>;
  TrustPilot: FC<TrustPilotProps>;
};

const DEFAULT_IMAGE_SET: HeroImageSrcSet = {
  desktop: {
    src: defaultHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: defaultHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: defaultHeroImageMobile,
    height: 614,
  },
};

const LandingHero: FC<LandingHeroProps> & LandingHeroSubcomponents = ({
  heroImageSrcSet = DEFAULT_IMAGE_SET,
  upliftedContent = false,
  centerContent = false,
  children,
  imageProps,
  ...boxProps
}: LandingHeroProps) => {
  const { desktop, mobile, tablet } = heroImageSrcSet;
  const topPadding = upliftedContent ? '16x' : '8x';

  return (
    <Box
      position="relative"
      {...(!upliftedContent && {
        height: ['auto', '90vh'],
        maxHeight: ['none', `${tablet.height}px`, `${desktop.height}px`],
      })}
      {...(upliftedContent && {
        minHeight: ['none', `${tablet.height}px`, `${desktop.height}px`],
        pb: ['0x', '8x'],
      })}
      {...(centerContent && {
        display: ['block', 'flex'],
        flexDirection: 'column',
      })}
      {...boxProps}
    >
      <Section
        paddingTop={centerContent ? ['6x', '0x'] : topPadding}
        position="relative"
        zIndex="1"
        {...(centerContent && {
          flex: '1',
          alignItems: ['stretch', 'center'],
        })}
      >
        {children}
      </Section>

      <Box
        position={['relative', 'absolute']}
        top="0"
        left="0"
        width="100%"
        height={[`auto`, '100%']}
        display={upliftedContent ? ['none', 'block'] : 'block'}
      >
        <Image
          webProps={{ src: [mobile?.src ?? tablet.src, tablet.src, desktop.src] }}
          description=""
          width="100%"
          height="100%"
          resizeMethod="cover"
          sx={{
            aspectRatio: ['1.25', 'auto'],
            objectPosition: ['100% 0%', '50% 50%'],
            ...imageProps?.sx,
          }}
          {...imageProps}
        />
      </Box>
    </Box>
  );
};

LandingHero.Content = ({
  upliftedContent = false,
  ...props
}: BoxProps & { upliftedContent?: boolean }): JSX.Element => {
  return (
    <Box
      background="primary"
      borderRadius={['0px', 'primary']}
      pt={['0x', '8x']}
      pb="8x"
      px={['0x', '8x']}
      width={['100%', upliftedContent ? '427px' : '480px']}
      {...props}
    >
      {props.children}
    </Box>
  );
};

LandingHero.Display = (props: DisplayProps): JSX.Element => {
  return (
    <Display
      data-qa-id="landing-hero-title"
      testID="landing-hero-title"
      marginTop="4x"
      webProps={{ as: 'h1', responsive: false }}
      size="500"
      {...props}
    >
      {props.children}
    </Display>
  );
};

LandingHero.Text = (props: HeadingProps): JSX.Element => {
  return (
    <Heading
      data-qa-id="landing-hero-subtitle"
      webProps={{ as: 'h2' }}
      fontWeight="regular"
      size="300"
      {...props}
    >
      {props.children}
    </Heading>
  );
};

LandingHero.SearchForm = (props: SearchFormProps): JSX.Element => {
  return <LandingSearchForm {...props} />;
};

LandingHero.TrustPilot = ({
  justifyContent = ['center', 'left'],
  ...props
}: TrustPilotProps & { justifyContent?: FlexboxSystemProps['justifyContent'] }): JSX.Element => {
  return (
    <Flex justifyContent={justifyContent}>
      <TrustPilot sizeOverride={SizeOverride.medium} paddingX="0x" {...props} />
    </Flex>
  );
};

export default LandingHero;
